// src/pages/outsourcing_management/InquiryConvertPreview.tsx
//
// The purchase-order email inside the inquiry dialog's convert mode. The
// dialog swaps into a convert view in place (no second dialog), and this is
// the middle column of that view - the purchase order as it would actually go
// out, next to the form producing it, exactly where the draft preview sits
// while the inquiry is being edited.
//
// The supplier, date and note being chosen go to the render endpoint, which
// heads the saved inquiry's document as the purchase order with those values
// swapped in. Debounced so typing the note does not fire one request per
// keystroke; nothing is rendered until both a supplier and a delivery date
// are picked, mirroring what the convert itself requires.
//
// Rendered through the same EmailPreview frame the rest of the dialog uses,
// so it fills the email column at its natural width - no scaling, no cut-off.
import { FunctionComponent, useContext, useEffect, useRef, useState } from "react";
import { Skeleton, Typography } from "@mui/material";
import AppStateContext from "contexts/AppStateContext";
import {
  apiPreviewPurchaseOrder,
  InquiryItemPriceType,
} from "util/network/Inquiries";
import { EmailPreview as EmailPreviewFrame } from "components/Inquiry/EmailPreviewPanel";

type PropsType = {
  inquiryId: number;
  partnerId?: number;
  deadline: string;
  deliveryMethod: string;
  message: string;
  itemPrices?: InquiryItemPriceType[];
  transportCostCents?: number;
};

const InquiryConvertPreview: FunctionComponent<PropsType> = ({
  inquiryId,
  partnerId,
  deadline,
  deliveryMethod,
  message,
  itemPrices,
  transportCostCents,
}) => {
  const { token } = useContext(AppStateContext);
  const [html, setHtml] = useState<string>("");
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  // The effect below is keyed on this rather than the array: the form hands
  // up a new one on every keystroke anywhere, prices touched or not.
  const pricesKey = JSON.stringify(itemPrices ?? []);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (!partnerId || !deadline) {
      setHtml("");
      setState("loading");
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    setState("loading");
    timer.current = setTimeout(() => {
      apiPreviewPurchaseOrder(token, inquiryId, {
        partnerId,
        deadline,
        deliveryMethod: deliveryMethod.trim() || undefined,
        message: message.trim() || undefined,
        itemPrices: JSON.parse(pricesKey),
        transportCostCents,
      })
        .then(async (res) => {
          // The render endpoint answers text/plain on failure, so a 200 is
          // the only thing separating an email from an error shown as one.
          if (!res.ok) throw new Error(String(res.status));
          const text = await res.text();
          setHtml(text);
          setState("ready");
        })
        .catch(() => setState("error"));
    }, 400);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [
    token,
    inquiryId,
    partnerId,
    deadline,
    deliveryMethod,
    message,
    pricesKey,
    transportCostCents,
  ]);

  return (
    <>
      <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
        Purchase order preview
      </Typography>
      {!partnerId || !deadline ? (
        <Typography variant="body2" color="text.secondary">
          Pick a supplier and a delivery date to see the purchase order.
        </Typography>
      ) : state === "loading" ? (
        <Skeleton variant="rectangular" width="100%" height={320} />
      ) : state === "error" ? (
        <Typography variant="body2" color="error">
          The purchase order preview could not be generated.
        </Typography>
      ) : (
        <EmailPreviewFrame
          html={html}
          title={`Purchase order preview for inquiry ${inquiryId}`}
        />
      )}
    </>
  );
};

export default InquiryConvertPreview;
